// File: frontend/src/components/pages/home/FAQ.js

import React, { useState } from 'react';
import { useLanguage } from '../../../hooks/useLanguage';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, MessageCircle, Mail } from 'lucide-react';
import ScrollAnimation from '../../common/ScrollAnimation';
import styles from './FAQ.module.css';

const FAQ = () => {
  const { t, parseHTML } = useLanguage();
  const navigate = useNavigate();
  const [openItems, setOpenItems] = useState([]);

  const FAQ_ITEMS = [
    {
      id: 'ai-analysis',
      question: t('faq.ai_analysis.question', 'How does the AI analyze my policies?'),
      answer: t('faq.ai_analysis.answer', 'Upload your policy PDF or image. Our OCR extracts data automatically. Review and confirm the details. Our AI then analyzes coverage gaps, suggests improvements, and answers your questions in plain English.')
    },
    {
      id: 'data-security',
      question: t('faq.data_security.question', 'Is my data secure?'),
      answer: t('faq.data_security.answer', 'Yes. We use end-to-end encryption (AES-256) for all data. Your documents are stored in secure AWS infrastructure with SOC 2 compliance. We never share your data with third parties or insurance companies without your explicit consent.')
    },
    {
      id: 'data-selling',
      question: t('faq.data_selling.question', 'Do you sell my data?'),
      answer: t('faq.data_selling.answer', 'Never. We make money from subscriptions, not data sales. Your policies are yours. We use them only to provide you with insights and alerts. We have no partnerships with insurers or data brokers.')
    },
    {
      id: 'insurer-visibility',
      question: t('faq.insurer_visibility.question', 'Can my insurance company see I\'m using Allegory?'),
      answer: t('faq.insurer_visibility.answer', 'No. We don\'t share any information with insurers. Your usage is completely private. We\'re not affiliated with any insurance company.')
    },
    {
      id: 'cancellation',
      question: t('faq.cancellation.question', 'What happens to my data if I cancel?'),
      answer: t('faq.cancellation.answer', 'Your data remains accessible until the end of your billing period. After that, we permanently delete everything — no backups, no archives, no exceptions. You can export your data anytime before canceling.')
    },
    {
      id: 'supported-types',
      question: t('faq.supported_types.question', 'What insurance types do you support?'),
      answer: t('faq.supported_types.answer', 'We support Auto, Home, Pet, Travel, Commercial, Life, Health, and Pension insurance. Our AI is trained on thousands of policy documents across all major carriers in the US and Canada.')
    }
  ];

  const toggleItem = (id) => {
    setOpenItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleEmailClick = () => {
    navigate('/contact');
  };

  const handleChatClick = () => {
    window.dispatchEvent(new Event('openChatWidget'));
  };

  return (
    <section id="faq" className={styles.section}>
      <div className={styles.container}>
        <ScrollAnimation>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.imageContainer}>
                <img 
                  src="/images/faq-question-mark.png" 
                  alt="" 
                  className={styles.questionMarkImage}
                  loading="lazy"
                />
              </div>
              <div className={styles.headerText}>
                <h2 className={styles.title}>
                  {t('faq.title', 'Questions? We have answers.')}
                </h2>
                <p className={styles.subtitle}>
                  {t('faq.subtitle', 'Everything you need to know about protecting your insurance data and getting the most out of Allegory.')}
                </p>
              </div>
            </div>
          </div>
        </ScrollAnimation>

        <div className={styles.faqList}>
          {FAQ_ITEMS.map((item, index) => (
            <ScrollAnimation key={item.id} delay={index * 50}>
              <div
                className={`${styles.faqItem} ${openItems.includes(item.id) ? styles.open : ''}`}
              >
                <button
                  className={styles.question}
                  onClick={() => toggleItem(item.id)}
                  aria-expanded={openItems.includes(item.id)}
                  aria-controls={`answer-${item.id}`}
                >
                  <span className={styles.questionText}>{item.question}</span>
                  <ChevronDown size={24} strokeWidth={2} className={styles.chevron} />
                </button>

                <div
                  id={`answer-${item.id}`}
                  className={styles.answerContainer}
                  aria-hidden={!openItems.includes(item.id)}
                >
                  <div className={styles.answer}>
                    <p>{item.answer}</p>
                  </div>
                </div>
              </div>
            </ScrollAnimation>
          ))}
        </div>

        <ScrollAnimation delay={300}>
          <div className={styles.footer}>
            <div className={styles.footerContent}>
              <h3 className={styles.footerTitle}>
                {t('faq.footer.title', 'Still have questions?')}
              </h3>
              <p className={styles.footerText}>
                {t('faq.footer.text', 'Our support team is here to help you get the most out of your insurance wallet.')}
              </p>
              <div className={styles.footerActions}>
                <button
                  className={styles.chatButton}
                  onClick={handleChatClick}
                >
                  <MessageCircle size={20} strokeWidth={2} />
                  {t('faq.footer.chat_button', 'Start Live Chat')}
                </button>
                <button
                  className={styles.emailButton}
                  onClick={handleEmailClick}
                >
                  <Mail size={20} strokeWidth={2} />
                  {t('faq.footer.email_button', 'Send Email')}
                </button>
              </div>
            </div>
          </div>
        </ScrollAnimation>
      </div>
    </section>
  );
};

export default FAQ;